import AddIcon from "@mui/icons-material/Add";
import { ReactElement, useState } from "react";

import {
  CreateResponseScriptGroupInput,
  CreateResponseScriptGroupMutation,
  CreateResponseScriptGroupMutationVariables,
  CreateResponseScriptInput,
  CreateResponseScriptMutation,
  CreateResponseScriptMutationVariables,
  FieldsForResponseScriptGroupFragment,
  ImportExternalResponseScriptsMutation,
  ImportExternalResponseScriptsMutationVariables,
  ResponseScriptImportJob,
} from "~frontend/__generated__/graphql";
import {
  CREATE_RESPONSE_SCRIPT,
  CREATE_RESPONSE_SCRIPT_GROUP,
  IMPORT_EXTERNAL_RESPONSE_SCRIPTS,
  LIST_RESPONSE_SCRIPT_GROUPS,
} from "~frontend/graphql/scripts";
import { GET_SUBACCOUNT_ADMIN_FIELDS } from "~frontend/graphql/subaccounts";
import {
  SubaccountAdminData,
  useFeatureFlag,
} from "~frontend/providers/LoggedInUserProvider/context";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import useMutation from "~frontend/util/useMutation";

import { FlexBox } from "../FlexBox";
import { AddButton } from "../Table";
import AddGroupDialog from "./AddGroupDialog";
import AddScriptDialog from "./AddScriptDialog";
import ImportScriptsDialog from "./ImportScriptsDialog";
import { ResponseScriptExternalImportSummary } from "./ResponseScriptExternalImportSummary";

export const ResponseScriptAddButtons = ({
  groups,
  subaccount,
}: {
  groups: FieldsForResponseScriptGroupFragment[];
  subaccount: SubaccountAdminData;
}): ReactElement => {
  const responseScriptImportsEnabled = useFeatureFlag("responseScriptImports");
  const nonCacheClearingClient = useNonCacheClearingApolloClient();

  const [addGroupDialogOpen, toggleAddGroupDialog] = useState(false);

  const [addGroup] = useMutation<
    CreateResponseScriptGroupMutation,
    CreateResponseScriptGroupMutationVariables
  >(CREATE_RESPONSE_SCRIPT_GROUP, {
    onErrorSnackbarMessage: "Error adding group",
    refetchQueries: [LIST_RESPONSE_SCRIPT_GROUPS],
    awaitRefetchQueries: true,
  });

  async function handleAddGroup(
    values: Omit<CreateResponseScriptGroupInput, "subaccountId">
  ) {
    const { errors } = await addGroup({
      variables: {
        input: {
          title: values.title,
          includeInCampaignByDefault: values.includeInCampaignByDefault,
          subaccountId: subaccount.id,
          campaignId: null,
        },
      },
    });
    if (errors) {
      throw errors;
    } else {
      toggleAddGroupDialog(false);
    }
  }

  const [addScriptDialogOpen, toggleAddScriptDialog] = useState(false);

  const [addScript] = useMutation<
    CreateResponseScriptMutation,
    CreateResponseScriptMutationVariables
  >(CREATE_RESPONSE_SCRIPT, {
    refetchQueries: [LIST_RESPONSE_SCRIPT_GROUPS],
    awaitRefetchQueries: true,
  });

  async function handleAddScript(
    values: Omit<CreateResponseScriptInput, "subaccountId">
  ) {
    const { errors } = await addScript({
      variables: {
        input: {
          title: values.title,
          script: values.script,
          mediaUploadId: values.mediaUploadId,
          groupIds: values.groupIds,
          subaccountId: subaccount.id,
          tagIds: values.tagIds,
          keywordsToAdd: values.keywordsToAdd,
        },
      },
    });
    if (errors) {
      throw errors;
    } else {
      toggleAddScriptDialog(false);
    }
  }

  const [importScriptDialogOpen, toggleImportScriptsDialog] = useState(false);
  const [importScripts] = useMutation<
    ImportExternalResponseScriptsMutation,
    ImportExternalResponseScriptsMutationVariables
  >(IMPORT_EXTERNAL_RESPONSE_SCRIPTS, {
    refetchQueries: [LIST_RESPONSE_SCRIPT_GROUPS, GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client: nonCacheClearingClient,
  });

  async function handleImportScripts(values: any) {
    const { errors } = await importScripts({
      variables: {
        input: {
          gsheetUrl: values.gsheetUrl,
          subaccountId: subaccount.id,
        },
      },
    });
    if (errors) {
      throw errors;
    } else {
      toggleImportScriptsDialog(false);
    }
  }

  return (
    <>
      <FlexBox
        justifyContent="space-between"
        alignItems="flex-end"
        gap={6}
        sx={{ my: 2 }}
      >
        {responseScriptImportsEnabled ? (
          <ResponseScriptExternalImportSummary
            job={
              subaccount.responseScriptImportToSubaccountJob as ResponseScriptImportJob
            }
          />
        ) : (
          <div />
        )}

        <FlexBox
          justifyContent="end"
          alignItems="center"
          gap={1}
          sx={{
            "& .MuiButton-startIcon": {
              mr: 0.5,
            },
          }}
        >
          <AddButton
            key="add-response-script-group"
            id="add-response-script-group"
            icon={<AddIcon />}
            onClick={() => toggleAddGroupDialog(true)}
            label="Add Group"
          />
          <AddButton
            key="add-response-script"
            id="add-response-script"
            icon={<AddIcon />}
            onClick={() => toggleAddScriptDialog(true)}
            label="Add Response"
            disabled={!groups.length}
            tooltipTitle={
              !groups.length ? "Start by adding a group" : undefined
            }
          />
          {responseScriptImportsEnabled ? (
            <AddButton
              key="import-response-scripts"
              id="import-response-scripts"
              icon={<AddIcon />}
              onClick={() => toggleImportScriptsDialog(true)}
              label="Import Responses"
              tooltipTitle="Import response scripts from a Google sheet"
              disabled={
                subaccount.responseScriptImportToSubaccountJob?.status ===
                "RUNNING"
              }
            />
          ) : null}
        </FlexBox>
      </FlexBox>

      <AddGroupDialog
        open={addGroupDialogOpen}
        onClose={() => toggleAddGroupDialog(false)}
        onSubmit={handleAddGroup}
        allowSettingSubaccountDefault
      />
      <AddScriptDialog
        open={addScriptDialogOpen}
        scriptGroups={groups}
        onClose={() => toggleAddScriptDialog(false)}
        onSubmit={handleAddScript}
      />
      <ImportScriptsDialog
        open={importScriptDialogOpen}
        onClose={() => toggleImportScriptsDialog(false)}
        onSubmit={handleImportScripts}
      />
    </>
  );
};
